import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@mui/icons-material/Home';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import type { TranslationKeys } from '../../../i18n';

const NotFound: React.FC = () => {
  const { t } = useTranslation<TranslationKeys>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 3, sm: 6 },
          textAlign: 'center',
          borderRadius: 3,
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
        }}
      >
        {/* 404 Icon */}
        <Box sx={{ mb: 3 }}>
          <SearchOffIcon
            sx={{
              fontSize: { xs: 80, sm: 120 },
              color: '#e04a50',
              opacity: 0.8,
            }}
          />
        </Box>

        {/* 404 Heading */}
        <Typography
          variant={isMobile ? 'h4' : 'h2'}
          component="h1"
          sx={{
            fontWeight: 'bold',
            color: '#333',
            mb: 2,
          }}
        >
          404
        </Typography>

        {/* Main Message */}
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="h2"
          sx={{
            fontWeight: 600,
            color: '#555',
            mb: 2,
          }}
        >
          {t('page_not_found')}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: '#666',
            mb: 4,
            maxWidth: '500px',
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          {t('page_not_found_description')}
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            onClick={handleGoHome}
            startIcon={<HomeIcon />}
            sx={{
              backgroundColor: '#c4262e',
              textTransform: 'none',
              px: 3,
              py: 1,
              minWidth: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#a01e24',
              },
            }}
          >
            {t('go_to_dashboard')}
          </Button>

          <Button
            variant="outlined"
            onClick={handleGoBack}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              minWidth: { xs: '100%', sm: 'auto' },
              borderColor: '#c4262e',
              color: '#c4262e',
              '&:hover': {
                borderColor: '#a01e24',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {t('go_back')}
          </Button>
        </Box>

        {/* Additional Help Text */}
        <Typography
          variant="body2"
          sx={{
            color: '#888',
            mt: 4,
            fontStyle: 'italic',
          }}
        >
          {t('page_not_found_help')}
        </Typography>
      </Paper>
    </Container>
  );
};

export default NotFound;
